import { getActiveLanguage, languageStore } from '../i18n/languageStore.js';
import { authStorage } from '../tenancy/authStorage.js';
import { tryResolveTenantSlug } from '../tenancy/tenant.js';
import { MANAGED_DETAIL_REQUEST } from './request.js';

export const DETAIL_CACHE_TTL_MS = 15_000;
export const DETAIL_MAX_CONCURRENT = 4;
export const DETAIL_MAX_QUEUED = 50;

const PRIORITIES = {
  visible: 0,
  normal: 1,
  prefetch: 2,
};

const createAbortError = (message = 'The detail request was cancelled.') => {
  const error = new Error(message);
  error.name = 'AbortError';
  error.code = 'request_cancelled';
  return error;
};

const createQueueFullError = () => {
  const error = new Error('Sunt prea multe detalii în așteptare. Reîncercați elementul vizibil.');
  error.name = 'DetailQueueFullError';
  error.code = 'detail_queue_full';
  error.temporary = true;
  return error;
};

const canonicalizeUrl = (input) => {
  const base = typeof window !== 'undefined'
    ? window.location.origin
    : 'http://localhost';
  const url = new URL(String(input), base);
  const params = [...url.searchParams.entries()]
    .sort(([keyA, valueA], [keyB, valueB]) => (
      keyA.localeCompare(keyB) || valueA.localeCompare(valueB)
    ));

  url.search = '';
  params.forEach(([key, value]) => url.searchParams.append(key, value));
  url.hash = '';
  return url.toString();
};

const stableSerialize = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => (
      `${JSON.stringify(key)}:${stableSerialize(value[key])}`
    )).join(',')}}`;
  }
  return JSON.stringify(value);
};

const fingerprint = (value) => {
  const source = stableSerialize(value);
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const readStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const readSessionJson = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

let observedToken;
let sessionGeneration = 0;
let internalSessionId = 'anonymous-0';

const rotateInternalSession = (token) => {
  observedToken = token;
  sessionGeneration += 1;
  const randomPart = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  internalSessionId = `${sessionGeneration}-${randomPart}`;
};

const tenantUuidFromToken = (token) => {
  const parts = typeof token === 'string' ? token.split('.') : [];
  return parts[0] === 't1' && parts[1] ? parts[1] : null;
};

const defaultContext = ({ init } = {}) => {
  const authorization = new Headers(init?.headers || {}).get('Authorization') || '';
  const requestToken = authorization.replace(/^Token\s+/i, '').trim() || null;
  const token = requestToken || authStorage.getToken();
  if (token !== observedToken) rotateInternalSession(token);

  const tenantSlug = tryResolveTenantSlug();
  const storagePrefix = tenantSlug ? `atrux:${tenantSlug}:` : null;
  const profile = (storagePrefix ? readStoredJson(`${storagePrefix}user`) : null) || {};
  const permissions = (storagePrefix ? readSessionJson(`${storagePrefix}features`) : null) || {};
  const userIdentity = profile.id
    || profile.user_id
    || profile.phone_number
    || profile.email
    || 'unknown-user';

  return {
    tenant: tenantUuidFromToken(token) || tenantSlug || 'unknown-tenant',
    user: String(userIdentity),
    session: internalSessionId,
    language: getActiveLanguage() || 'ro',
    permissions: fingerprint(permissions),
  };
};

const contextKey = (context) => JSON.stringify([
  context.tenant,
  context.user,
  context.session,
  context.language,
  context.permissions,
]);

const parseRetryAfterMs = (value, now = Date.now()) => {
  if (value === null || value === undefined || value === '') return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;

  const date = Date.parse(value);
  if (Number.isNaN(date)) return null;
  return Math.max(0, date - now);
};

const parseErrorPayload = async (response) => {
  const contentType = response.headers?.get?.('content-type') || '';
  if (contentType.includes('json')) {
    return response.json().catch(() => null);
  }
  const text = await response.text().catch(() => '');
  return text ? { detail: text } : null;
};

const createResponseError = async (response) => {
  const payload = await parseErrorPayload(response);
  const status = Number(response.status || 0);
  const code = payload?.code || payload?.error || null;
  const language = getActiveLanguage();
  const fallback = status === 503 || status === 429
    ? (language === 'en'
      ? 'The service is temporarily unavailable. Please try again.'
      : 'Serviciul este temporar indisponibil. Reîncercați.')
    : (payload?.detail || `Cererea nu a putut fi finalizată (${status}).`);
  const error = new Error(payload?.detail || payload?.message || fallback);
  error.name = 'DetailReadError';
  error.status = status;
  error.code = code;
  error.params = payload?.params || {};
  error.payload = payload;
  error.retryAfterMs = parseRetryAfterMs(response.headers?.get?.('retry-after'));
  error.temporary = status === 429 || status === 503;
  return error;
};

const defaultSleep = (milliseconds, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(createAbortError());
    return;
  }

  let timer;
  const cleanup = () => signal?.removeEventListener('abort', handleAbort);
  const handleComplete = () => {
    cleanup();
    resolve();
  };
  const handleAbort = () => {
    clearTimeout(timer);
    cleanup();
    reject(createAbortError());
  };
  timer = setTimeout(handleComplete, milliseconds);
  signal?.addEventListener('abort', handleAbort, { once: true });
});

const runtimeFetch = (url, init) => {
  const fetchFunction = typeof window !== 'undefined' && window.fetch
    ? window.fetch.bind(window)
    : globalThis.fetch?.bind(globalThis);
  if (!fetchFunction) throw new Error('Fetch API is not available');
  return fetchFunction(url, init);
};

export class DetailReadManager {
  constructor({
    fetchImpl = runtimeFetch,
    getContext = defaultContext,
    now = () => Date.now(),
    random = Math.random,
    sleep = defaultSleep,
    cacheTtlMs = DETAIL_CACHE_TTL_MS,
    maxConcurrent = DETAIL_MAX_CONCURRENT,
    maxQueued = DETAIL_MAX_QUEUED,
  } = {}) {
    this.fetchImpl = fetchImpl;
    this.getContext = getContext;
    this.now = now;
    this.random = random;
    this.sleep = sleep;
    this.cacheTtlMs = cacheTtlMs;
    this.maxConcurrent = maxConcurrent;
    this.maxQueued = maxQueued;
    this.cache = new Map();
    this.inFlight = new Map();
    this.queue = [];
    this.activeCount = 0;
    this.epoch = 0;
    this.resourceGenerations = new Map();
    this.consumerId = 0;
  }

  getStats() {
    return {
      active: this.activeCount,
      queued: this.queue.length,
      inFlight: this.inFlight.size,
      cached: this.cache.size,
    };
  }

  purgeExpiredCache() {
    const currentTime = this.now();
    [...this.cache.entries()].forEach(([key, cached]) => {
      if (cached.expiresAt <= currentTime) this.cache.delete(key);
    });
  }

  async getJson(url, init = {}, options = {}) {
    const method = String(init.method || 'GET').toUpperCase();
    if (method !== 'GET') {
      throw new Error('DetailReadManager accepts only safe GET requests.');
    }
    if (options.signal?.aborted || init.signal?.aborted) throw createAbortError();

    const canonicalUrl = canonicalizeUrl(url);
    const resourceKey = `${method} ${canonicalUrl}`;
    const currentContext = this.getContext({ url: canonicalUrl, init });
    const key = `${resourceKey}\u001f${contextKey(currentContext)}`;
    this.purgeExpiredCache();
    const cached = this.cache.get(key);

    if (options.forceRefresh) this.cache.delete(key);
    if (!options.forceRefresh && cached) {
      if (cached.expiresAt > this.now()) return cached.value;
      this.cache.delete(key);
    }

    let entry = this.inFlight.get(key);
    if (!entry) {
      const priority = Object.prototype.hasOwnProperty.call(PRIORITIES, options.priority)
        ? options.priority
        : 'normal';
      if (this.activeCount >= this.maxConcurrent && this.queue.length >= this.maxQueued) {
        if (priority !== 'visible' || !this.evictQueuedPrefetch()) {
          throw createQueueFullError();
        }
      }

      const headers = new Headers(init.headers || {});
      if (!headers.has('Accept')) headers.set('Accept', 'application/json');
      if (!headers.has('Accept-Language') && currentContext.language) {
        headers.set('Accept-Language', currentContext.language);
      }

      entry = {
        key,
        resourceKey,
        url: canonicalUrl,
        init: {
          ...init,
          method: 'GET',
          headers,
          signal: undefined,
          [MANAGED_DETAIL_REQUEST]: true,
        },
        priority,
        priorityValue: PRIORITIES[priority],
        consumers: new Map(),
        controller: new AbortController(),
        state: 'queued',
        epoch: this.epoch,
        resourceGeneration: this.resourceGenerations.get(resourceKey) || 0,
        cacheTtlMs: options.cacheTtlMs ?? this.cacheTtlMs,
      };
      this.inFlight.set(key, entry);
      this.queue.push(entry);
      this.queue.sort((left, right) => left.priorityValue - right.priorityValue);
    }

    const consumerPromise = this.addConsumer(entry, options.signal || init.signal);
    this.pump();
    return consumerPromise;
  }

  addConsumer(entry, signal) {
    if (signal?.aborted) return Promise.reject(createAbortError());
    const id = ++this.consumerId;

    return new Promise((resolve, reject) => {
      const handleAbort = () => {
        const consumer = entry.consumers.get(id);
        if (!consumer) return;
        entry.consumers.delete(id);
        consumer.cleanup();
        reject(createAbortError());
        if (entry.consumers.size === 0) {
          const cancelIfStillUnused = () => {
            if (entry.consumers.size === 0) this.cancelEntry(entry, createAbortError());
          };
          if (typeof queueMicrotask === 'function') queueMicrotask(cancelIfStillUnused);
          else Promise.resolve().then(cancelIfStillUnused);
        }
      };
      const cleanup = () => signal?.removeEventListener('abort', handleAbort);
      entry.consumers.set(id, { resolve, reject, cleanup });
      signal?.addEventListener('abort', handleAbort, { once: true });
    });
  }

  evictQueuedPrefetch() {
    for (let index = this.queue.length - 1; index >= 0; index -= 1) {
      const entry = this.queue[index];
      if (entry.priority === 'prefetch') {
        this.cancelEntry(entry, createQueueFullError());
        return true;
      }
    }
    return false;
  }

  pump() {
    while (this.activeCount < this.maxConcurrent && this.queue.length > 0) {
      const entry = this.queue.shift();
      if (!entry || entry.state !== 'queued' || entry.consumers.size === 0) continue;
      entry.state = 'running';
      this.activeCount += 1;
      void this.runEntry(entry);
    }
  }

  retryDelay(error, retryIndex) {
    const jitter = Math.floor(this.random() * 500);
    if (Number.isFinite(error.retryAfterMs)) return Math.max(0, error.retryAfterMs) + jitter;
    return (retryIndex === 0 ? 5000 : 10000) + jitter;
  }

  async execute(entry) {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (entry.controller.signal.aborted) throw createAbortError();
      const response = await this.fetchImpl(entry.url, {
        ...entry.init,
        signal: entry.controller.signal,
      });
      if (response.ok) return response.json();

      const error = await createResponseError(response);
      const shouldRetry = (error.status === 429 || error.status === 503) && attempt < 2;
      if (!shouldRetry) throw error;
      await this.sleep(this.retryDelay(error, attempt), entry.controller.signal);
    }
    throw new Error('Unreachable detail retry state.');
  }

  async runEntry(entry) {
    try {
      const value = await this.execute(entry);
      const isCurrent = entry.epoch === this.epoch
        && entry.resourceGeneration === (this.resourceGenerations.get(entry.resourceKey) || 0)
        && !entry.controller.signal.aborted;
      if (isCurrent && entry.cacheTtlMs > 0) {
        this.cache.set(entry.key, {
          value,
          expiresAt: this.now() + entry.cacheTtlMs,
          resourceKey: entry.resourceKey,
        });
      }
      if (isCurrent) this.settleEntry(entry, 'resolve', value);
      else this.settleEntry(entry, 'reject', createAbortError('The detail result is no longer current.'));
    } catch (error) {
      const normalizedError = entry.controller.signal.aborted && error?.name !== 'AbortError'
        ? createAbortError()
        : error;
      this.settleEntry(entry, 'reject', normalizedError);
    } finally {
      this.activeCount = Math.max(0, this.activeCount - 1);
      this.pump();
    }
  }

  settleEntry(entry, action, value) {
    if (this.inFlight.get(entry.key) === entry) this.inFlight.delete(entry.key);
    entry.state = 'settled';
    entry.consumers.forEach((consumer) => {
      consumer.cleanup();
      consumer[action](value);
    });
    entry.consumers.clear();
  }

  cancelEntry(entry, error = createAbortError()) {
    if (!entry || entry.state === 'settled' || entry.state === 'cancelled') return;
    entry.state = 'cancelled';
    const queueIndex = this.queue.indexOf(entry);
    if (queueIndex >= 0) this.queue.splice(queueIndex, 1);
    if (this.inFlight.get(entry.key) === entry) this.inFlight.delete(entry.key);
    if (!entry.controller.signal.aborted) entry.controller.abort();
    entry.consumers.forEach((consumer) => {
      consumer.cleanup();
      consumer.reject(error);
    });
    entry.consumers.clear();
  }

  invalidateResource(url, method = 'GET') {
    const resourceKey = `${String(method).toUpperCase()} ${canonicalizeUrl(url)}`;
    this.resourceGenerations.set(
      resourceKey,
      (this.resourceGenerations.get(resourceKey) || 0) + 1,
    );
    [...this.cache.entries()].forEach(([key, cached]) => {
      if (cached.resourceKey === resourceKey) this.cache.delete(key);
    });
    [...this.inFlight.values()].forEach((entry) => {
      if (entry.resourceKey === resourceKey) {
        this.cancelEntry(entry, createAbortError('The resource changed while it was loading.'));
      }
    });
    this.pump();
  }

  invalidateAll() {
    this.epoch += 1;
    this.cache.clear();
    [...this.inFlight.values()].forEach((entry) => this.cancelEntry(entry));
    this.queue = [];
  }

  adoptLanguage(language) {
    this.cache.clear();
    [...this.inFlight.values()].forEach((entry) => {
      const previousKey = entry.key;
      const headers = new Headers(entry.init.headers || {});
      headers.set('Accept-Language', language);
      entry.init = { ...entry.init, headers };
      const nextContext = this.getContext({ url: entry.url, init: entry.init });
      const nextKey = `${entry.resourceKey}\u001f${contextKey(nextContext)}`;
      if (nextKey === previousKey) return;

      const collision = this.inFlight.get(nextKey);
      if (collision && collision !== entry) {
        this.cancelEntry(entry, createAbortError('The language changed while details were loading.'));
        return;
      }
      if (this.inFlight.get(previousKey) === entry) this.inFlight.delete(previousKey);
      entry.key = nextKey;
      this.inFlight.set(nextKey, entry);
    });
  }
}

export const detailReadManager = new DetailReadManager();

if (typeof window !== 'undefined') {
  const invalidateSessionContext = () => {
    rotateInternalSession(authStorage.getToken());
    detailReadManager.invalidateAll();
  };
  window.addEventListener('atrux:auth-changed', invalidateSessionContext);
  window.addEventListener('atrux:auth-revoked', invalidateSessionContext);
  window.addEventListener('atrux:permissions-changed', () => detailReadManager.invalidateAll());
  window.addEventListener('storage', (event) => {
    if (event.key?.startsWith('atrux:') && event.key.endsWith(':token')) {
      invalidateSessionContext();
    }
  });
}

languageStore.subscribe((snapshot, change) => {
  if (change?.previousLanguage && change.previousLanguage !== snapshot?.activeLanguage) {
    // apiFetch aborts and replays the network attempt in the new locale. Keep
    // consumers attached, but move the shared entry to the new locale key and
    // discard every cached result from the previous language.
    detailReadManager.adoptLanguage(snapshot.activeLanguage);
  }
});

export const invalidateDetailResource = (url) => detailReadManager.invalidateResource(url);
